/* eslint-disable react-hooks/exhaustive-deps */
import {Box, Button, Flex, Heading, Icon, Portal, Spinner, Text, useMediaQuery, VStack} from "@chakra-ui/react";
import {useEffect, useState} from "react";
import {Link as RouterLink, useParams} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {FiEdit, FiGlobe, FiLock} from "react-icons/fi";
import {GiSadCrab} from "react-icons/gi";
import {ItemsGrid} from "../components/ItemsGrid";
import {FabButton} from "../components/FabButton";
import AddItemModal from "../components/AddItemModal";
import EditItemModal from "../components/EditItemModal";
import ListModal from "../components/ListModal";
import {useAPI} from "../hooks/useAPI";
import {useUIManager} from "../hooks/useUIManager";
import {useBookingActions} from "../hooks/useBookingActions";
import {usePendingBooking} from "../hooks/usePendingBooking";
import {ItemType} from "../services/types";
import {ListResponse} from "../services/responses";

const VISIBILITY_ICON = {private: FiLock, link: FiGlobe, public: undefined} as const;

const ListPage = () => {
  const api = useAPI();
  const uiManager = useUIManager();
  const {slug, uuid} = useParams();

  const [isMd] = useMediaQuery("(min-width: 576px)");
  const [loaded, setLoaded] = useState(false);
  const [list, setList] = useState<ListResponse | null>(null);
  const [items, setItems] = useState<ItemType[]>([]);

  const isOwner = api.loggedIn && !!slug && api.loggedInUser?.username === slug;

  const {book, unbook} = useBookingActions(setItems);
  usePendingBooking(loaded, items, book);

  useEffect(() => {
    if (!uuid) {
      return;
    }

    // the add/edit item modals should target this list while it is open
    uiManager.setCurrentList(uuid);
    api.checkAuthorization()
      .then(() => api.getList(uuid))
      .then((response) => {
        setList(response);
        if (response === null) {
          setLoaded(true);
          return [] as ItemType[];
        }
        return api.getListItems(uuid);
      })
      .then((listItems) => {
        setItems(listItems.map((i) => ({
          uuid: i.uuid,
          name: i.name,
          description: i.description,
          image: i.image,
          link: i.link,
          booked: i.booked,
          booked_by_me: i.booked_by_me,
          booked_by: i.booked_by,
        })));
        setLoaded(true);
      })
      .finally(() => uiManager.toggleNeedReloadItems(false));
  }, [uuid, uiManager.needReloadItems, uiManager.needReloadLists]);

  // when leaving the list page, stop targeting the list
  useEffect(() => () => uiManager.setCurrentList(null), []);

  const icon = list ? VISIBILITY_ICON[list.visibility] : undefined;

  return <>
    {!loaded && (
      <Flex justifyContent="center" alignItems="center" flexGrow={1} mt="3rem">
        <Spinner size="xl"/>
      </Flex>
    )}

    {loaded && list === null && (
      <VStack gap={2}>
        <Icon as={GiSadCrab} boxSize="64px"/>
        <Heading textAlign="center">List not found</Heading>
        <Text textAlign="center" fontSize="lg" textColor="gray.500">This list doesn't exist or is not available.</Text>
        <Button as={RouterLink} to={slug ? `/${slug}` : "/"}>Back to profile</Button>
      </VStack>
    )}

    {loaded && list !== null && (<>
      <Box bg="white" w="100%" p={4} borderRadius="lg" mb={isMd ? 8 : 3}>
        <Flex justifyContent="space-between" alignItems="start" gap={3}>
          <Box>
            <Flex alignItems="center" gap={2}>
              {isOwner && icon && <Icon as={icon} color="gray.500" aria-label={list.visibility}/>}
              <Heading size={{base: "sm", sm: "md"}}>{list.name}</Heading>
            </Flex>
            {list.description && (
              <Text fontSize={{base: "xs", sm: "md"}} color="gray.600" mt={2} whiteSpace="pre-wrap">{list.description}</Text>
            )}
            <Button as={RouterLink} to={slug ? `/${slug}` : "/"} variant="link" size="sm" mt={2}>← Back to profile</Button>
          </Box>

          {isOwner && (
            <Flex gap={2}>
              <Button size={{base: "sm", sm: "md"}} leftIcon={<FiEdit/>}
                      onClick={() => uiManager.toggleListModal(true, list.uuid)}>Edit</Button>
              <Button size={{base: "sm", sm: "md"}} colorScheme="brand"
                      onClick={() => uiManager.openAddModal()}>Add item</Button>
            </Flex>
          )}
        </Flex>
      </Box>

      {!!items.length && (
        <ItemsGrid items={items} setItems={setItems} editable={isOwner}
                   persistSorting={(uuids) => api.updateListSorting(uuid as string, uuids)}
                   booking={{enabled: true, isOwner, onBook: book, onUnbook: unbook}}/>
      )}
      {!items.length && (
        <Box textAlign="center" mt="3rem">
          <VStack color="gray.500" fontSize={{base: "xs", md: "sm"}} gap={0}>
            <Text fontSize={{base: "36px", md: "48px"}}>¯\_(ツ)_/¯</Text>
            <Text>This list is empty.</Text>
            {isOwner && <Text>Add something you wish for this occasion.</Text>}
          </VStack>
        </Box>
      )}

      {isOwner && !isMd && <FabButton/>}
    </>)}

    <Portal>
      <AddItemModal/>
      <EditItemModal/>
      <ListModal/>
    </Portal>
  </>;
};

export default observer(ListPage);
